import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Loader2, FileText, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

export default function ContentTemplatePage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    sections: {
      introduction: '', service_explanation: '',
      production_process: '', city_context: '',
      faq_template: [], cta_text: ''
    }
  });

  const loadData = () => {
    setLoading(true);
    adminAPI.getTemplates().then(r => setTemplates(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ name: '', sections: { introduction: '', service_explanation: '', production_process: '', city_context: '', faq_template: [], cta_text: '' } });
    setEditing(null); setShowForm(false);
  };

  const startEdit = (t) => {
    setForm({ name: t.name, sections: t.sections || {} });
    setEditing(t.id); setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Sablon adi zorunlu'); return; }
    try {
      if (editing) { await adminAPI.updateTemplate(editing, form); toast.success('Sablon guncellendi'); }
      else { await adminAPI.createTemplate(form); toast.success('Sablon olusturuldu'); }
      resetForm(); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silinsin mi?')) return;
    try { await adminAPI.deleteTemplate(id); toast.success('Silindi'); loadData(); } catch { toast.error('Silinemedi'); }
  };

  const updateSection = (key, value) => {
    setForm(f => ({ ...f, sections: { ...f.sections, [key]: value } }));
  };

  const sectionLabels = {
    introduction: 'Giris',
    service_explanation: 'Hizmet Aciklamasi',
    production_process: 'Uretim Sureci',
    city_context: 'Sehir Baglami',
    cta_text: 'CTA Metni'
  };

  return (
    <div data-testid="admin-templates">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Icerik Sablonlari</h1>
          <p className="text-sm text-muted-foreground mt-1">Landing page icerikleri icin yeniden kullanilabilir sablonlar. 500-800 kelime onerilir.</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-template-btn">
          <Plus className="h-4 w-4 mr-2" /> Yeni Sablon
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Sablon Duzenle' : 'Yeni Icerik Sablonu'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Sablon Adi *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Orn: Lazer Kesim Sablonu" data-testid="template-name" /></div>
            {Object.entries(sectionLabels).map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <textarea
                  value={form.sections[key] || ''}
                  onChange={e => updateSection(key, e.target.value)}
                  rows={key === 'cta_text' ? 2 : 4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={`{sehir} ve {hizmet} yer tutucularini kullanabilirsiniz`}
                />
              </div>
            ))}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              Yer tutucular: {'{sehir}'} = Sehir adi, {'{hizmet}'} = Hizmet adi olarak otomatik degistirilir.
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="template-submit">{editing ? 'Guncelle' : 'Kaydet'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : templates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Henuz sablon olusturulmamis</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {templates.map(t => (
            <Card key={t.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[hsl(221,83%,22%)]" />
                  <div>
                    <span className="font-semibold">{t.name}</span>
                    <p className="text-xs text-muted-foreground">
                      {Object.values(t.sections || {}).filter(v => typeof v === 'string' && v.length > 0).length} bolum dolu
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
